import { useEffect } from 'react';
import { useAppSelector } from '../../app/state';
import { callAction } from '../../app/actions';

/**
 * THE APP'S ONE DISMISSIBLE BANNER, and every wording it carries.
 *
 * It began (plan 1.6) as the unreachable-link notice: a shared link pointed at something this
 * user cannot see, or at something that no longer exists — the map loads normally and this
 * says which of the two it was. Never a blank screen, and never the same wording for both:
 * "isn't available to you" is a permission fact, "no longer exists" is a deletion fact, and
 * the router decides between them AFTER permissions so a hidden node's existence never leaks
 * through a not-found.
 *
 * V0.34 gave it the concurrency half rather than a second banner component, because the app
 * has one place for "something happened that you should know about and did not ask for", and
 * two would compete for the same slot on screen:
 *   - `map-refreshed`  — somebody else changed the map and this view was quietly updated. It
 *     expires by itself (8 s): the map has already moved on and nothing is owed.
 *   - `edit-rejected`  — one of THIS user's changes was refused because somebody else saved
 *     first. It stays until dismissed and outranks every other wording, because it is the only
 *     one that says work was lost. There is deliberately no Retry button: the refresh has
 *     already replaced local state, so "please make the change again" is the honest
 *     instruction and a button that pretended otherwise would re-send a change against a map
 *     that no longer exists.
 * `app/mapSync.ts` owns the precedence between them; this component only draws what it is told.
 *
 * Styled off the toast family (`.saved`) — same fixed top-centre slot, same panel surface —
 * but it stays until dismissed, because "why did my link not work" is an answer somebody may
 * need to read after the map has finished drawing itself around it.
 */

const WORDING = {
  'not-permitted': "That page isn't available to you.",
  'not-found': 'That node or document no longer exists.',
  'map-refreshed': 'Someone else changed the map. Your view has been updated.',
  'edit-rejected':
    'Your last change was not saved: someone else saved first. The map now shows their version — please make the change again.',
  // V0.37: a brick was withdrawn behind an affordance already on screen. The detail line
  // names the missing permission(s), as the server reported them.
  'edit-forbidden':
    "Your last change was not saved: your account doesn't have the required permission. The map has been restored to the server's version.",
  // V0.43: parts of the map are hidden from this account, so a whole-tree tidy would publish
  // positions derived from a tree with branches missing. Says why rather than doing nothing.
  'realign-partial':
    'Realign is unavailable because parts of the map are hidden from your account. Moving nodes individually still works.',
  // V0.45: a 401 came back, so the login card is up. Says WHY it is, which is the whole
  // point — an expired session used to show an empty screen and no explanation. The detail
  // line names unsent edits when there were any.
  'session-ended': 'Your session ended — sign in again.',
  // V0.48: an administrator is restoring a backup, so every route is answering 503 for a
  // minute or two. Says the app is coming back, because the alternative — a scatter of
  // silently failed saves and empty panels — is what it replaces. It is the one wording that
  // ends on a FACT rather than a timer or a click: the server answering again clears it.
  maintenance: 'An administrator is restoring a backup. The app will be back in a moment.',
} as const;

export function RouteNotice({ host }: { host: HTMLElement }) {
  const notice = useAppSelector((s) => s.routeNotice);
  // Named only when the write path can honestly name it; null everywhere today. Rendered
  // beside the wording rather than in place of it, so the instruction never disappears.
  const detail = useAppSelector((s) => s.noticeDetail);

  useEffect(() => {
    host.classList.toggle('show', !!notice);
  }, [notice, host]);

  if (!notice) return null;

  return (
    <div className="route-note" role="status">
      <span>
        {WORDING[notice]}
        {detail ? <span className="route-note-detail"> {detail}</span> : null}
      </span>
      <button
        type="button"
        className="route-note-x"
        id="routenotice-dismiss"
        title="Dismiss"
        onClick={() => callAction('dismissRouteNotice')}
      >
        ×
      </button>
    </div>
  );
}
